import {
  CompanyContact,
  CompanyNameCheck,
  CompanyNameProblem,
  CompanyNameVerdict,
  DbotEvent,
  DbotEventDispatcher,
  DecisionContext,
  DsePrekontrollErrorItem,
  isDocumentLocked,
  isDocumentSigned,
  isDseSystemUnavailable,
  makeCountryRadioFieldModel,
  MatrikkelAddress,
  MatrikkelAddressVerification,
  MatrikkelObjectType,
  MatrikkelVerificationStatus,
  NACE_SEARCH_MIN_QUERY_LENGTH,
  NaceSearchResult,
  ProviderValidity,
} from "../common/commonDomain";
import { DocumentType } from "@/common/documents";
import { makeAutoObservable } from "mobx";
import { DecisionType } from "@/common/decisions";
import {
  LocalDateFieldModel,
  NumberFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import { debounce, defer, identity, uniqBy } from "lodash";
import { TMessage } from "@/i18n/tt";
import { ShareTypeCode, shareTypeName } from "@/common/share";
import { toNumber } from "@/utils/numbers";
import { NumberOfShares } from "@/shareholderRegister/transactions/commonTransactionsDomain";
import { getRoleTypeLabel, RoleType } from "@/types/enums/RoleType";
import { ActorsSelectorModel, ActorType, loadActors, SelectedActor } from "@/types/SelectActors";
import { newUuid } from "@/utils/uuid";
import { getFullName, validateName } from "@/utils/Roles";
import { toCompanyRoleReference } from "@/utils/UrnReference";
import { norway } from "@/utils/countries";
import { norwegianSocialSecurityNumberValidator } from "@/utils/other";
import { Language } from "@/i18n/languages";
import { removeExtraSpaces } from "@/utils/strings";
import { CompanyRole, SuggestedCompany } from "@/types/CompanyInfo";
import { emailValidator, isFailure, pending, PhoneNumber, Result } from "@/common/common";
import { PhoneInputModel } from "@/components-v2/PhoneInput";
import {
  ArticleSummary,
  autoArticles,
  IncorporationArticles,
  IncorporationArticlesModel,
} from "./incorporationArticlesDomain";

export type Incorporation = {
  type: DecisionType.incorporation;
  numberOfCompanies?: number;
  companyNames: string[];
  incorporationDate?: string;
  industry: string;
  industryCode?: string;
  aktivitetBransjeKode?: string;
  statutoryPurpose: string;
  ownershipType: OwnershipType;
  owners: IncorporationOwner[];
  shareNominalValue: number;
  shareTypes: NumberOfShares[];
  signers?: Signer[];
  addressIsSameAsParent: boolean;
  address?: string;
  zipCode?: string;
  city?: string;
  country?: string;
  addressObjType?: MatrikkelObjectType;
  boardIsSameAsParent: boolean;
  boardComposition?: IncorporationCompanyRole[];
  isContinuation: boolean;
  hasEmployees: boolean;
  isNamingAuto: boolean;
  hasAuditor: boolean;
  auditor?: Auditor;
  hasAccountant: boolean;
  accountant?: Accountant;
  capitalConfirmer?: CapitalConfirmer;
  deadlineForContribution?: string;
  notificationEmail?: string;
  invoiceEmail?: string;
  notificationPhoneNumber?: PhoneNumber;
  customArticles?: IncorporationArticles;
};

export type Auditor = {
  company: CompanyContact;
  representativeName?: string;
  email?: string;
  cost?: number;
};

export type Accountant = {
  company: CompanyContact;
  representativeName?: string;
  email?: string;
};

export type CapitalConfirmer = {
  company: CompanyContact;
  representativeName?: string;
  representativeEmail?: string;
};

enum OwnershipType {
  WHOLLY_OWNED_SUBSIDIARY_OF_PARENT = "WHOLLY_OWNED_SUBSIDIARY_OF_PARENT",
  HAS_OTHER_FOUNDERS = "HAS_OTHER_FOUNDERS",
}

/** A Matrikkel-verified address override; `objectType` is absent only on drafts saved before it was recorded. */
export type CustomBusinessAddress = {
  addressText: string;
  postalCode: string;
  city: string;
  objectType?: MatrikkelObjectType;
};

export type IncorporationCompanyRole = {
  id?: string;
  role: RoleType;
  name: string;
  socialSecurityNumber?: string;
  personReference?: string;
  country?: string;
};

export type BoardSection = "board" | "ceo";

export type BoardAssignment = {
  id: string;
  role: RoleType;
  name: string;
  socialSecurityNumber?: string;
  personReference?: string;
  country?: string;
};

export type BoardPerson = {
  name: string;
  socialSecurityNumber?: string;
  personReference?: string;
  country?: string;
};

export type IncorporationBoardDialogState =
  | { type: "none" }
  | { type: "select-person"; model: ActorsSelectorModel }
  | { type: "add-person"; model: AddBoardPersonModel }
  | { type: "assign-role"; model: AssignBoardRoleModel };

export type IncorporationSignerDialogState =
  | { type: "none" }
  | { type: "select-person"; model: ActorsSelectorModel };

export type Signer = {
  id?: string;
  type: RoleType | "other";
  name: string;
  email: string;
  socialSecurityNumber?: string;
  personReference?: string;
};

export type AdditionalRoleSigner = {
  id: string;
  kind: "role";
  role: CompanyRole;
};

export type AdditionalCustomSigner = {
  id: string;
  kind: "custom";
  nameModel: StringFieldModel;
  emailModel: StringFieldModel;
  socialSecurityNumberModel: StringFieldModel;
  personReference?: string;
};

export type AdditionalSigner = AdditionalRoleSigner | AdditionalCustomSigner;

function createCustomSigner(signer?: Signer): AdditionalCustomSigner {
  const shownNumber = signer?.socialSecurityNumber ?? "";
  return {
    id: signer?.id ?? newUuid(),
    kind: "custom",
    nameModel: new StringFieldModel({ value: signer?.name ?? "", required: true }),
    emailModel: new StringFieldModel({
      value: signer?.email ?? "",
      required: true,
      validator: emailValidator,
    }),
    socialSecurityNumberModel: new StringFieldModel({
      value: shownNumber,
      required: true,
      validator: (value) =>
        shownNumber !== "" && withoutSpaces(value) === withoutSpaces(shownNumber)
          ? undefined
          : norwegianSocialSecurityNumberValidator(value),
    }),
    personReference: signer?.personReference,
  };
}

function withoutSpaces(value: string): string {
  return value.replace(/\s/g, "");
}

function parentRoleForActor(roles: CompanyRole[], actor: SelectedActor): CompanyRole | undefined {
  return roles.find(
    (r) =>
      actor.reference === toCompanyRoleReference(r.companyroleId) ||
      (actor.inCompany?.companyRoleIds ?? []).includes(r.companyroleId),
  );
}

function signerReference(signer: AdditionalSigner): string | undefined {
  return signer.kind === "role"
    ? toCompanyRoleReference(signer.role.companyroleId)
    : signer.personReference;
}

export type IncorporationOwner = PersonOwner | CompanyOwner;
export type PersonOwner = {
  type: "PERSON";
  socialSecurityNumber: string;
  name: string;
};

export type CompanyOwner = {
  type: "COMPANY";
  orgNo: string;
  name: string;
};

const INVESTMENT_INDUSTRY_CODE = "00.000";
export const CUSTOM_INDUSTRY_CODE = "custom-industry";
const MIN_CUSTOM_INDUSTRY_LENGTH = 4;
export const MAX_CUSTOM_INDUSTRY_LENGTH = 100;

type NaceSearchState = "idle" | "searching" | "completed";

function validateCustomIndustry(value: string): TMessage | undefined {
  const length = value.trim().length;
  if (length < MIN_CUSTOM_INDUSTRY_LENGTH) {
    return { key: "text-too-short" };
  }
  return length > MAX_CUSTOM_INDUSTRY_LENGTH ? { key: "text-too-long" } : undefined;
}

export type IndustryShortcut = {
  label: TMessage;
  industry: NaceSearchResult;
  statutoryPurpose: string;
};

export const INDUSTRY_SHORTCUTS: IndustryShortcut[] = [
  {
    label: { key: "decisions.incorporation.industry-shortcut-hylleselskap" },
    industry: {
      code: INVESTMENT_INDUSTRY_CODE,
      aktivitetBransjeKode: "2832",
      label: "Hylleselskap, opprettet for videresalg.",
      description: "",
      category: "",
    },
    statutoryPurpose: "Selskapet er stiftet med sikte på videresalg.",
  },
  {
    label: { key: "decisions.incorporation.industry-shortcut-holding" },
    industry: {
      code: INVESTMENT_INDUSTRY_CODE,
      aktivitetBransjeKode: "2835",
      label: "Holdingselskap.",
      description: "",
      category: "",
    },
    statutoryPurpose:
      "Selskapets formål er å eie aksjer og andeler i andre selskaper samt tilhørende virksomhet.",
  },
  {
    label: { key: "decisions.incorporation.industry-shortcut-investering" },
    industry: {
      code: INVESTMENT_INDUSTRY_CODE,
      aktivitetBransjeKode: "2830",
      label: "Investering i aksjer og verdipapirer.",
      description: "",
      category: "",
    },
    statutoryPurpose:
      "Selskapets formål er investering i andre selskaper, verdipapirer og annen relatert virksomhet.",
  },
  {
    label: { key: "decisions.incorporation.industry-shortcut-eiendom" },
    industry: {
      code: "68.110",
      aktivitetBransjeKode: "2391",
      label: "Kjøp og salg av egen fast eiendom.",
      description: "",
      category: "",
    },
    statutoryPurpose: "Selskapets formål er kjøp, salg, utleie og forvaltning av fast eiendom.",
  },
];

export type CompanyNameAvailability = "unknown" | "checking" | "available" | "unavailable";

type PendingCompanyNameCheck = {
  index: number;
  normalizedName: string;
};

/** Verdict for the address currently in the field when no Matrikkel suggestion has been picked. */
type BusinessAddressCheck = {
  street: string;
  zip: string;
  status: MatrikkelVerificationStatus | "checking";
  address?: MatrikkelAddress;
};

/** Brreg's verdict on a picked auditor or accountant; "checking" while the answer is in flight. */
type ProviderCheck = {
  organisationNumber: string;
  status: ProviderValidity | "checking";
};

export class IncorporationForm {
  readonly type = DecisionType.incorporation;
  readonly context: DecisionContext<Incorporation>;
  readonly data?: Incorporation;
  readonly dispatch: DbotEventDispatcher;
  numberOfCompaniesModel: RadioFieldModel<number>;
  companyNameModels: StringFieldModel[] = [];
  companyNameAvailability: CompanyNameAvailability[] = [];
  companyNameProblems: CompanyNameProblem[][] = [];
  checkedCompanyNames: string[] = [];
  industrySearchModel: StringFieldModel;
  searchNace: (input: string) => void;
  suggestedNaceResults: NaceSearchResult[];
  selectedNace?: NaceSearchResult;
  naceSearchState: NaceSearchState = "idle";
  customIndustryModel?: StringFieldModel;
  industrySearchBeforeCustomDialog = "";
  statutoryPurposeModel: StringFieldModel;
  ownersModel: RadioFieldModel<OwnershipType>;
  shareNominalValueModel: NumberFieldModel;
  shareClassModel: RadioFieldModel<ShareTypeCode>;
  signerModel: RadioFieldModel<CompanyRole | undefined>;
  additionalSigners: AdditionalSigner[] = [];
  signerDialogState: IncorporationSignerDialogState = { type: "none" };
  numberOfSharesModel: NumberFieldModel;
  businessAddressModel: StringFieldModel;
  searchMatrikkelAddresses: (input: string) => void;
  suggestedMatrikkelAddresses: MatrikkelAddress[] = [];
  selectedBusinessAddress?: CustomBusinessAddress;
  isSearchingMatrikkelAddresses = false;
  businessAddressCheck?: BusinessAddressCheck;
  private businessAddressCheckSource?: object;
  boardSelectModel: RadioFieldModel<"parent" | "other">;
  boardAssignments: BoardAssignment[] = [];
  boardDialogState: IncorporationBoardDialogState = { type: "none" };
  boardChairError?: TMessage;
  continuationSelectModel: RadioFieldModel<boolean>;
  hasEmployeesSelectModel: RadioFieldModel<boolean>;
  auditorSelectModel: RadioFieldModel<boolean>;
  auditorSearchModel: StringFieldModel;
  searchAuditorCompanies: (input: string) => void;
  suggestedAuditorCompanies: SuggestedCompany[] = [];
  selectedAuditorCompany?: SuggestedCompany;
  auditorRepresentativeNameModel: StringFieldModel;
  auditorEmailModel: StringFieldModel;
  auditorCostModel: NumberFieldModel;
  auditorCheck?: ProviderCheck;
  private auditorCheckSource?: object;
  deadlineForContributionModel: LocalDateFieldModel;
  accountantSelectModel: RadioFieldModel<boolean>;
  accountantSearchModel: StringFieldModel;
  searchAccountantCompanies: (input: string) => void;
  suggestedAccountantCompanies: SuggestedCompany[] = [];
  selectedAccountantCompany?: SuggestedCompany;
  accountantCheck?: ProviderCheck;
  private accountantCheckSource?: object;
  accountantRepresentativeNameModel: StringFieldModel;
  accountantEmailModel: StringFieldModel;
  capitalConfirmerSearchModel: StringFieldModel;
  searchCapitalConfirmerCompanies: (input: string) => void;
  suggestedCapitalConfirmerCompanies: SuggestedCompany[] = [];
  selectedCapitalConfirmerCompany?: SuggestedCompany;
  capitalConfirmerRepresentativeNameModel: StringFieldModel;
  capitalConfirmerRepresentativeEmailModel: StringFieldModel;
  capitalConfirmerValidationError?: TMessage;
  notificationEmailModel: StringFieldModel;
  invoiceEmailModel: StringFieldModel;
  notificationPhoneModel: PhoneInputModel;
  prekontrollResult?: Result<undefined, TMessage>;
  prekontrollErrors?: DsePrekontrollErrorItem[];
  prekontrollErrorCode?: string;
  articlesModel: IncorporationArticlesModel;
  private capitalConfirmerSearchSource = {};
  private accountantSearchSource = {};
  isNamingAuto = true;
  pendingCompanyNameChecks = new Map<object, PendingCompanyNameCheck>();

  constructor(
    context: DecisionContext<Incorporation>,
    dispatch: DbotEventDispatcher,
    data?: Incorporation,
  ) {
    this.context = context;
    this.data = data;
    this.dispatch = dispatch;
    const companyNames = data?.companyNames ?? [];
    const numberOfCompanies =
      data?.numberOfCompanies ?? (companyNames.length > 0 ? companyNames.length : 3);
    const primaryShareType = data?.shareTypes[0];
    this.isNamingAuto = data?.isNamingAuto ?? true;

    this.numberOfCompaniesModel = new RadioFieldModel<number>({
      value: numberOfCompanies,
      options: Array.from({ length: 50 }, (_, i) => i + 1),
      idFn: identity,
      labelFn: identity,
      onChange: (n) => this.updateCompanyNameModels(n),
    });

    this.ownersModel = this.createOwnersModel(data);
    this.updateCompanyNameModels(numberOfCompanies, companyNames);
    this.selectedNace = data?.industry
      ? {
          code: data.industryCode ?? CUSTOM_INDUSTRY_CODE,
          aktivitetBransjeKode: data.aktivitetBransjeKode,
          label: data.industry,
          description: "",
          category: "",
        }
      : undefined;
    this.industrySearchModel = new StringFieldModel({
      value: data?.industry ?? "",
    });
    this.suggestedNaceResults = [];
    this.searchNace = debounce((input: string) => this.findNace(input), 250);
    this.statutoryPurposeModel = new StringFieldModel({
      value: data?.statutoryPurpose ?? "",
    });
    this.shareNominalValueModel = new NumberFieldModel({
      value: data?.shareNominalValue ?? 1,
      isPositiveOrZero: true,
    });
    this.shareClassModel = new RadioFieldModel({
      value: parseShareTypeCode(primaryShareType?.sharetypeCode) ?? ShareTypeCode.COMMON,
      options: Object.values(ShareTypeCode),
      idFn: identity,
      labelFn: (o) => shareTypeName(o),
    });

    this.numberOfSharesModel = new NumberFieldModel({
      value: primaryShareType?.numberOfShares ?? 30000,
      isInteger: true,
      isPositiveOrZero: true,
    });
    const savedSigners = data?.signers;
    const primarySigner = savedSigners
      ? this.parentRoleForSavedSigner(savedSigners[0])
      : this.defaultPrimarySigner;
    this.signerModel = this.createSignerModel(primarySigner);
    this.additionalSigners = (savedSigners ?? [])
      .slice(primarySigner ? 1 : 0)
      .map((s) => this.restoreAdditionalSigner(s))
      .filter((s) => s.kind !== "role" || !this.isPrimarySigner(s));
    this.selectedBusinessAddress = savedBusinessAddress(data);
    this.businessAddressModel = new StringFieldModel({
      value: this.selectedBusinessAddress
        ? formatMatrikkelAddress(this.selectedBusinessAddress)
        : this.parentBusinessAddress,
    });
    this.searchMatrikkelAddresses = debounce(
      (input: string) => this.findMatrikkelAddresses(input),
      250,
    );
    this.boardSelectModel = this.createBoardSelectModel(data);
    this.boardAssignments = createBoardAssignments(data);
    if (this.boardAssignments.length === 0 && data?.boardIsSameAsParent !== false) {
      this.boardAssignments = parentBoardAssignments(this.companyRoles);
    }
    this.continuationSelectModel = this.createContinuationSelectModel(data);
    this.hasEmployeesSelectModel = this.createHasEmployeesSelectModel(data);
    this.auditorSelectModel = new RadioFieldModel<boolean>({
      value: data?.hasAuditor ?? false,
      options: [false, true],
      idFn: identity,
      labelFn: (v) =>
        v
          ? { key: "decisions.incorporation.auditor-specify" }
          : { key: "decisions.incorporation.auditor-none" },
    });
    this.selectedAuditorCompany = mapIncorporationAuditorToCompany(data);
    this.auditorSearchModel = new StringFieldModel({
      value: this.selectedAuditorCompany?.name ?? "",
    });
    this.suggestedAuditorCompanies = [];
    this.searchAuditorCompanies = debounce(
      (input: string) => this.findAuditorCompanies(input),
      250,
    );
    this.auditorRepresentativeNameModel = new StringFieldModel({
      value: data?.auditor?.representativeName ?? "",
    });
    this.auditorEmailModel = new StringFieldModel({
      value: data?.auditor?.email ?? "",
      validator: emailValidator,
    });
    this.auditorCostModel = new NumberFieldModel({
      value: data?.auditor?.cost ?? 0,
    });
    this.accountantSelectModel = new RadioFieldModel<boolean>({
      value: data?.hasAccountant ?? false,
      options: [false, true],
      idFn: identity,
      labelFn: (v) =>
        v
          ? { key: "decisions.incorporation.accountant-specify" }
          : { key: "decisions.incorporation.accountant-none" },
    });
    this.selectedAccountantCompany = mapIncorporationAccountantToCompany(data);
    this.accountantSearchModel = new StringFieldModel({
      value: this.selectedAccountantCompany?.name ?? "",
    });
    this.suggestedAccountantCompanies = [];
    this.searchAccountantCompanies = debounce(
      (input: string) => this.findAccountantCompanies(input),
      250,
    );
    this.accountantRepresentativeNameModel = new StringFieldModel({
      value: data?.accountant?.representativeName ?? "",
    });
    this.accountantEmailModel = new StringFieldModel({
      value: data?.accountant?.email ?? "",
      validator: emailValidator,
    });
    this.deadlineForContributionModel = new LocalDateFieldModel({
      value: data?.deadlineForContribution,
    });
    this.selectedCapitalConfirmerCompany = mapIncorporationCapitalConfirmerToCompany(data);
    this.capitalConfirmerSearchModel = new StringFieldModel({
      value: this.selectedCapitalConfirmerCompany?.name ?? "",
    });
    this.searchCapitalConfirmerCompanies = debounce(
      (input: string) => this.findCapitalConfirmerCompanies(input),
      250,
    );
    this.capitalConfirmerRepresentativeNameModel = new StringFieldModel({
      value: data?.capitalConfirmer?.representativeName ?? "",
    });
    this.capitalConfirmerRepresentativeEmailModel = new StringFieldModel({
      value: data?.capitalConfirmer?.representativeEmail ?? "",
      validator: emailValidator,
    });
    this.notificationEmailModel = new StringFieldModel({
      value: data?.notificationEmail ?? context.user.email ?? "",
      validator: emailValidator,
    });
    this.invoiceEmailModel = new StringFieldModel({
      value: data?.invoiceEmail ?? context.user.email ?? "",
      validator: emailValidator,
    });
    this.notificationPhoneModel = new PhoneInputModel({
      value: data?.notificationPhoneNumber ?? context.user.phoneNumber,
      locale: context.locale,
    });
    this.articlesModel = new IncorporationArticlesModel(dispatch, data?.customArticles);
    makeAutoObservable(
      this,
      {
        context: false,
        data: false,
        dispatch: false,
        pendingCompanyNameChecks: false,
        searchAccountantCompanies: false,
        searchAuditorCompanies: false,
        searchCapitalConfirmerCompanies: false,
        searchMatrikkelAddresses: false,
        searchNace: false,
      },
      { autoBind: true },
    );
  }

  toggleNamingAuto(): void {
    this.isNamingAuto = !this.isNamingAuto;
    if (this.isNamingAuto) {
      this.applyAutoNamingFromFirstName();
    }
    this.validateCompanyNames();
  }

  handleEvent(event: DbotEvent): void {
    switch (event.type) {
      case "dbot/search-companies-result": {
        if (event.source === this) {
          this.suggestedAuditorCompanies = event.result;
        } else if (event.source === this.accountantSearchSource) {
          this.suggestedAccountantCompanies = event.result;
        } else if (event.source === this.capitalConfirmerSearchSource) {
          this.suggestedCapitalConfirmerCompanies = event.result;
        }
        break;
      }
      case "dbot/check-company-name-result": {
        this.applyCompanyNameCheckResult(event.source, event.result);
        break;
      }
      case "dbot/check-provider-validity-result": {
        if (event.source === this.auditorCheckSource && this.auditorCheck !== undefined) {
          this.auditorCheck = { ...this.auditorCheck, status: event.result };
        } else if (
          event.source === this.accountantCheckSource &&
          this.accountantCheck !== undefined
        ) {
          this.accountantCheck = { ...this.accountantCheck, status: event.result };
        }
        break;
      }
      case "dbot/search-nace-result": {
        if (event.source === this && this.naceSearchState === "searching") {
          this.suggestedNaceResults = event.result;
          this.naceSearchState = "completed";
        }
        break;
      }
      case "dbot/search-municipalities-result": {
        this.articlesModel.handleEvent(event);
        break;
      }
      case "dbot/search-matrikkel-address-result": {
        if (event.source === this) {
          this.suggestedMatrikkelAddresses = event.result;
          this.isSearchingMatrikkelAddresses = false;
        }
        break;
      }
      case "dbot/verify-matrikkel-address-result": {
        if (event.source === this.businessAddressCheckSource) {
          this.applyBusinessAddressVerification(event.result);
        }
        break;
      }
      case "dbot/prekontroll-incorporation": {
        if (event.decisionId === this.context.decisionId) {
          this.prekontrollResult = pending();
          this.prekontrollErrors = undefined;
          this.prekontrollErrorCode = undefined;
        }
        break;
      }
      case "dbot/prekontroll-incorporation-result": {
        if (event.decisionId === this.context.decisionId) {
          this.prekontrollResult = event.result;
          this.prekontrollErrors = event.errors;
          this.prekontrollErrorCode = event.code;
        }
        break;
      }
    }
  }

  get prekontrollError(): TMessage | undefined {
    return this.prekontrollResult !== undefined && isFailure(this.prekontrollResult)
      ? this.prekontrollResult.error
      : undefined;
  }

  get prekontrollSystemUnavailable(): boolean {
    return isDseSystemUnavailable(this.prekontrollErrorCode);
  }

  createHasEmployeesSelectModel(data?: Incorporation) {
    return new RadioFieldModel({
      value: data?.hasEmployees ?? false,
      options: [false, true],
      idFn: identity,
      labelFn: (v) => (v ? { key: "yes" } : { key: "no" }),
    });
  }

  createContinuationSelectModel(data?: Incorporation) {
    return new RadioFieldModel({
      value: data?.isContinuation ?? false,
      options: [false, true],
      idFn: identity,
      labelFn: (v) => (v ? { key: "yes" } : { key: "no" }),
    });
  }

  createBoardSelectModel(data?: Incorporation): RadioFieldModel<"parent" | "other"> {
    const labels: Record<"parent" | "other", TMessage> = {
      parent: {
        key: "decisions.incorporation.board-parent",
        params: {
          company: this.mainCompany.name,
        },
      },
      other: {
        key: "decisions.incorporation.board-other",
      },
    };

    return new RadioFieldModel({
      value: data?.boardIsSameAsParent === false ? "other" : "parent",
      options: ["parent", "other"],
      idFn: identity,
      labelFn: (o) => labels[o],
      onChange: (v) => {
        if (v === "parent") {
          this.boardChairError = undefined;
        }
      },
    });
  }

  get isOtherBoard(): boolean {
    return this.boardSelectModel.value === "other";
  }

  get boardChair(): BoardAssignment | undefined {
    return this.boardAssignments.find((a) => a.role === RoleType.CHAIRPERSON);
  }

  get styreAssignments(): BoardAssignment[] {
    return this.boardAssignments.filter((a) => a.role !== RoleType.CEO);
  }

  get dagligLedere(): BoardAssignment[] {
    return this.boardAssignments.filter((a) => a.role === RoleType.CEO);
  }

  get duplicateEntryIds(): string[] {
    return (
      this.context.contextData[this.mainCompany.orgNo]?.inputStates.find(
        (s) => s.decisionId === this.context.decisionId,
      )?.duplicateEntryIds ?? []
    );
  }

  get duplicateBoardPersonError(): TMessage | undefined {
    return this.boardAssignments.some((a) => this.duplicateEntryIds.includes(a.id))
      ? {
          key: "decisions.electionOfCompanyBoard.boardChange.dialog.socialSecurityNumber.existing-ssn",
        }
      : undefined;
  }

  get duplicateSignerError(): TMessage | undefined {
    return this.additionalSigners.some((s) => this.duplicateEntryIds.includes(s.id))
      ? {
          key: "decisions.electionOfCompanyBoard.boardChange.dialog.socialSecurityNumber.existing-ssn",
        }
      : undefined;
  }

  get boardCompositionPayload(): IncorporationCompanyRole[] {
    const byRole = (role: RoleType) => this.boardAssignments.filter((a) => a.role === role);
    return [
      ...byRole(RoleType.CHAIRPERSON),
      ...byRole(RoleType.MEMBER),
      ...byRole(RoleType.DEPUTY_MEMBER),
      ...byRole(RoleType.CEO),
    ].map(toIncorporationRole);
  }

  boardExcludedActors(section: BoardSection): string[] {
    return this.boardAssignments
      .filter((a) => (a.role === RoleType.CEO) === (section === "ceo"))
      .flatMap((a) => (a.personReference === undefined ? [] : [a.personReference]));
  }

  availableBoardRoles(assignmentId?: string): RoleType[] {
    const isTaken = (role: RoleType) =>
      this.boardAssignments.some((a) => a.role === role && a.id !== assignmentId);
    return [
      ...(isTaken(RoleType.CHAIRPERSON) ? [] : [RoleType.CHAIRPERSON]),
      ...(isTaken(RoleType.CEO) ? [] : [RoleType.CEO]),
      RoleType.MEMBER,
      RoleType.DEPUTY_MEMBER,
    ];
  }

  boardRolesFor(section: BoardSection): RoleType[] {
    return section === "ceo"
      ? [RoleType.CEO]
      : this.availableBoardRoles().filter((role) => role !== RoleType.CEO);
  }

  openSelectBoardPersonDialog(section: BoardSection) {
    this.boardDialogState = {
      type: "select-person",
      model: new ActorsSelectorModel({
        title: {
          key: "decisions.electionOfCompanyBoard.boardChange.button.item.add.portfolio.title",
        },
        organisationNumber: this.context.orgNo,
        tabs: [ActorType.BOARD_MEMBER, ActorType.CEO, ActorType.SHAREHOLDER],
        excludeActors: this.boardExcludedActors(section),
        excludeCompanies: true,
        isMultiSelect: false,
        defaultCompanyFilterValue: null,
        onLoad: (type, onSuccess, onFailure) =>
          loadActors(type, onSuccess, onFailure, this.dispatch, this.context.orgNo),
        onSubmit: ([actor]: SelectedActor[]) =>
          this.openAssignBoardRoleDialog(
            {
              name: actor.name,
              socialSecurityNumber: actor.socialSecurityNumber,
              personReference: actor.reference,
            },
            section,
          ),
        onClose: this.closeBoardDialog,
      }),
    };
  }

  openAddBoardPersonDialog(section: BoardSection) {
    this.boardDialogState = {
      type: "add-person",
      model: new AddBoardPersonModel(
        this.context.locale,
        this.boardRolesFor(section),
        this.addBoardAssignment,
        this.closeBoardDialog,
      ),
    };
  }

  openAssignBoardRoleDialog(person: BoardPerson, section: BoardSection) {
    const roles = this.boardRolesFor(section);
    this.boardDialogState = {
      type: "assign-role",
      model: new AssignBoardRoleModel(
        person.name,
        roles,
        roles[0],
        (role) => this.addBoardAssignment(person, role),
        this.closeBoardDialog,
      ),
    };
  }

  addBoardAssignment(person: BoardPerson, role: RoleType) {
    this.boardAssignments = [
      ...this.boardAssignments,
      {
        id: newUuid(),
        role,
        name: person.name,
        socialSecurityNumber: person.socialSecurityNumber?.trim() || undefined,
        personReference: person.personReference,
        country: person.country,
      },
    ];
    this.boardSelectModel.setValue("other");
    this.clearBoardChairErrorIfResolved();
    this.closeBoardDialog();
  }

  updateBoardAssignment(id: string, role: RoleType) {
    this.boardAssignments = this.boardAssignments.map((a) => (a.id === id ? { ...a, role } : a));
    this.boardSelectModel.setValue("other");
    this.clearBoardChairErrorIfResolved();
    this.closeBoardDialog();
  }

  removeBoardAssignment(id: string) {
    this.boardAssignments = this.boardAssignments.filter((a) => a.id !== id);
    this.boardSelectModel.setValue("other");
  }

  closeBoardDialog() {
    this.boardDialogState = { type: "none" };
  }

  private clearBoardChairErrorIfResolved() {
    if (this.boardChair !== undefined) {
      this.boardChairError = undefined;
    }
  }

  get parentBusinessAddress(): string {
    const company = this.context.contextData[this.mainCompany.orgNo]?.initialState.company;
    return formatBusinessAddress(
      company?.businessAddress1,
      company?.businessZip,
      company?.businessCity,
    );
  }

  /** The address is the parent's whenever the field is empty or still shows the parent's address. */
  get isParentBusinessAddress(): boolean {
    const value = this.businessAddressModel.inputValue.trim();
    return value === "" || value === this.parentBusinessAddress;
  }

  get isCustomBusinessAddress(): boolean {
    return !this.isParentBusinessAddress && this.selectedBusinessAddress !== undefined;
  }

  /**
   * A typed address only counts once it matches the Matrikkel suggestion that was picked — Brreg
   * rejects any forretningsadresse that is not spelled exactly as Matrikkelen has it.
   */
  get isBusinessAddressFromMatrikkel(): boolean {
    if (this.isParentBusinessAddress) {
      return true;
    }
    const selected = this.selectedBusinessAddress;
    return (
      selected !== undefined &&
      formatMatrikkelAddress(selected) === this.businessAddressModel.inputValue.trim()
    );
  }

  setBusinessAddressSearch(input: string) {
    this.businessAddressModel.setInputValue(input);
    this.selectedBusinessAddress = undefined;
    this.businessAddressModel.setError(undefined);
    this.businessAddressCheck = undefined;
    this.searchMatrikkelAddresses(input);
  }

  onBusinessAddressBlur() {
    this.businessAddressModel.commit();
    this.verifyBusinessAddress();
  }

  /**
   * The parent's address reaches us as `businessAddress1` + `businessAddress2` joined with ", ", and
   * because `businessAddress2` defaults to "" that leaves a trailing ", " on the stored value. Verify
   * the raw street instead of the formatted display string.
   */
  get businessAddressToVerify(): { street: string; zip: string } | undefined {
    if (this.selectedBusinessAddress !== undefined) {
      return undefined;
    }
    const value = this.businessAddressModel.inputValue.trim();
    if (value === "") {
      return undefined;
    }
    if (value === this.parentBusinessAddress) {
      const company = this.context.contextData[this.mainCompany.orgNo]?.initialState.company;
      const street = company?.businessAddress1?.trim().replace(/,$/, "").trim();
      const zip = company?.businessZip?.trim();
      return street && zip ? { street, zip } : undefined;
    }
    const match = /^(.+?),\s*(\d{4})\s+(.+)$/.exec(value);
    return match ? { street: match[1].trim(), zip: match[2] } : undefined;
  }

  init() {
    this.verifyBusinessAddress();
    this.checkAuditor();
    this.checkAccountant();
  }

  verifyBusinessAddress() {
    const target = this.businessAddressToVerify;
    if (target === undefined) {
      this.businessAddressCheck = undefined;
      this.businessAddressCheckSource = undefined;
      return;
    }
    if (
      this.businessAddressCheck?.street === target.street &&
      this.businessAddressCheck.zip === target.zip
    ) {
      return;
    }
    this.businessAddressCheckSource = {};
    this.businessAddressCheck = { ...target, status: "checking" };
    this.dispatch([
      {
        type: "dbot/verify-matrikkel-address",
        address: target.street,
        postalCode: target.zip,
        // Read back off `this`: mobx proxies the assigned object, and the result handler compares by
        // identity against the proxy.
        source: this.businessAddressCheckSource,
      },
    ]);
  }

  private applyBusinessAddressVerification(result: MatrikkelAddressVerification) {
    const check = this.businessAddressCheck;
    if (check === undefined) {
      return;
    }
    this.businessAddressCheck = { ...check, status: result.status, address: result.address };
    // A verified address the user typed or pasted becomes the picked address, so its objtype is
    // persisted. The inherited one is not adopted: applyForm sends no address when it stays the
    // parent's, so there is nothing to attach an objtype to.
    if (
      result.status === "VERIFIED" &&
      result.address !== undefined &&
      this.businessAddressModel.inputValue.trim() !== this.parentBusinessAddress
    ) {
      this.selectedBusinessAddress = result.address;
    }
    // Surfaced as soon as the verdict lands, not held back until validate(): the whole point is to
    // catch a bad inherited address before the form is filled in and sent for signing.
    this.businessAddressModel.setError(this.businessAddressError);
  }

  /** Undefined when the address is acceptable. Pending and outage verdicts do not block. */
  get businessAddressError(): TMessage | undefined {
    if (this.selectedBusinessAddress !== undefined && this.isBusinessAddressFromMatrikkel) {
      return undefined;
    }
    const check = this.businessAddressCheck;
    if (check === undefined) {
      return this.isParentBusinessAddress
        ? undefined
        : { key: "decisions.incorporation.business-address-select-from-matrikkel" };
    }
    const address = `${check.street}, ${check.zip}`;
    switch (check.status) {
      case "NOT_FOUND":
        return {
          key: "decisions.incorporation.business-address-not-in-matrikkel",
          params: { address },
        };
      case "AMBIGUOUS":
        return {
          key: "decisions.incorporation.business-address-ambiguous-in-matrikkel",
          params: { address },
        };
      default:
        return undefined;
    }
  }

  selectBusinessAddress(address: CustomBusinessAddress) {
    this.selectedBusinessAddress = address;
    this.businessAddressCheck = undefined;
    this.businessAddressCheckSource = undefined;
    this.businessAddressModel.setAndCommitInputValue(formatMatrikkelAddress(address));
    this.businessAddressModel.setError(undefined);
  }

  findMatrikkelAddresses(input: string) {
    this.isSearchingMatrikkelAddresses = input.trim().length >= 3;
    this.dispatch([{ type: "dbot/search-matrikkel-address", input, source: this }]);
  }

  /** Empties the field; an empty field means the new company keeps the parent's address. */
  clearBusinessAddress() {
    this.selectedBusinessAddress = undefined;
    this.suggestedMatrikkelAddresses = [];
    this.isSearchingMatrikkelAddresses = false;
    this.businessAddressCheck = undefined;
    this.businessAddressCheckSource = undefined;
    this.businessAddressModel.setAndCommitInputValue("");
    this.businessAddressModel.setError(undefined);
  }

  private get defaultPrimarySigner(): CompanyRole | undefined {
    const roles = this.companyRoles;
    return roles.find((r) => r.role === RoleType.CHAIRPERSON) ?? roles[0];
  }

  private parentRoleForSavedSigner(signer: Signer | undefined): CompanyRole | undefined {
    if (!signer) {
      return undefined;
    }
    const roles = this.companyRoles;
    return (
      roles.find((r) => r.role === signer.type && getFullName(r) === signer.name) ??
      roles.find((r) => r.role === signer.type)
    );
  }

  createSignerModel(value: CompanyRole | undefined): RadioFieldModel<CompanyRole | undefined> {
    return new RadioFieldModel<CompanyRole | undefined>({
      value,
      options: this.companyRoles,
      idFn: (r) => String(r?.companyroleId ?? ""),
      onChange: () => {
        this.additionalSigners = this.additionalSigners.filter(
          (s) => s.kind !== "role" || !this.isPrimarySigner(s),
        );
      },
      labelFn: (r) =>
        r
          ? {
              key: "decisions.incorporation.signer-role",
              params: {
                name: getFullName(r),
                role: getRoleTypeLabel(r.role),
                company: this.mainCompany.name,
              },
            }
          : { translated: "" },
    });
  }

  removePrimarySigner() {
    this.signerModel.setValue(undefined);
  }

  addAdditionalSigner() {
    this.appendAdditionalSigner(createCustomSigner());
  }

  private appendAdditionalSigner(signer: AdditionalSigner) {
    this.additionalSigners = [...this.additionalSigners, signer];
    this.signerModel.setError(undefined);
  }

  removeAdditionalSigner(id: string) {
    this.additionalSigners = this.additionalSigners.filter((s) => s.id !== id);
  }

  get hasSigners(): boolean {
    return this.signerModel.value !== undefined || this.additionalSigners.length > 0;
  }

  restoreAdditionalSigner(signer: Signer): AdditionalSigner {
    const role =
      signer.type !== "other"
        ? this.companyRoles.find((r) => r.role === signer.type && getFullName(r) === signer.name)
        : undefined;
    return role ? { id: newUuid(), kind: "role", role } : createCustomSigner(signer);
  }

  openSelectSignerDialog() {
    this.signerDialogState = {
      type: "select-person",
      model: new ActorsSelectorModel({
        title: {
          key: "decisions.electionOfCompanyBoard.boardChange.button.item.add.portfolio.title",
        },
        organisationNumber: this.context.orgNo,
        tabs: [ActorType.BOARD_MEMBER, ActorType.CEO, ActorType.SHAREHOLDER],
        excludeActors: this.signerExcludedActors,
        excludeCompanies: true,
        isMultiSelect: false,
        defaultCompanyFilterValue: this.context.orgNo,
        onLoad: (type, onSuccess, onFailure) =>
          loadActors(type, onSuccess, onFailure, this.dispatch, this.context.orgNo),
        onSubmit: (selectedActors: SelectedActor[]) => this.addSignerFromActor(selectedActors[0]),
        onClose: this.closeSignerDialog,
      }),
    };
  }

  addSignerFromActor(actor: SelectedActor) {
    const role = parentRoleForActor(this.companyRoles, actor);
    const signer: AdditionalSigner = role
      ? { id: newUuid(), kind: "role", role }
      : createCustomSigner({
          type: "other",
          name: actor.name,
          email: actor.email ?? "",
          socialSecurityNumber: actor.socialSecurityNumber ?? "",
          personReference: actor.reference,
        });
    if (!this.isAlreadySigner(signer)) {
      this.appendAdditionalSigner(signer);
    }
    this.closeSignerDialog();
  }

  isAlreadySigner(signer: AdditionalSigner): boolean {
    const reference = signerReference(signer);
    return reference !== undefined && this.signerExcludedActors.includes(reference);
  }

  isPrimarySigner(signer: AdditionalSigner): boolean {
    const primary = this.signerModel.value;
    return (
      primary !== undefined &&
      signerReference(signer) === toCompanyRoleReference(primary.companyroleId)
    );
  }

  get signerExcludedActors(): string[] {
    return [
      ...(this.signerModel.value
        ? [toCompanyRoleReference(this.signerModel.value.companyroleId)]
        : []),
      ...this.additionalSigners.flatMap((s) => {
        const reference = signerReference(s);
        return reference === undefined ? [] : [reference];
      }),
    ];
  }

  closeSignerDialog() {
    this.signerDialogState = { type: "none" };
  }

  createOwnersModel(data?: Incorporation) {
    const value = data?.ownershipType ?? OwnershipType.WHOLLY_OWNED_SUBSIDIARY_OF_PARENT;
    const ownersLabels: Record<OwnershipType, TMessage> = {
      WHOLLY_OWNED_SUBSIDIARY_OF_PARENT: {
        key: "decisions.incorporation.ownerParent",
        params: { company: this.mainCompany.name },
      },
      HAS_OTHER_FOUNDERS: { key: "decisions.incorporation.ownerOther" },
    };

    return new RadioFieldModel<OwnershipType>({
      value,
      options: Object.values(OwnershipType),
      idFn: identity,
      labelFn: (o) => ownersLabels[o],
      disabledFn: (o) => o === OwnershipType.HAS_OTHER_FOUNDERS,
    });
  }

  get mainCompany() {
    return this.context.companies[0];
  }

  get newCompanyName() {
    const model = this.companyNameModels[0];
    return model ? model.validatedValue || model.inputValue : "";
  }

  get companyRoles() {
    return (this.context.contextData[this.mainCompany.orgNo]?.initialState.roles ?? []).filter(
      (r) => r.role !== RoleType.GENERAL_MEETING_SIGNER,
    );
  }

  get chairpersonName() {
    return getFullName(this.companyRoles.find((r) => r.role === RoleType.CHAIRPERSON));
  }

  get ceoName() {
    return getFullName(this.companyRoles.find((r) => r.role === RoleType.CEO));
  }

  get isLocked(): boolean {
    return this.context.isLocked || this.isIncorporationDocumentSentForSigning;
  }

  /**
   * Once the incorporation document has been sent for signing, the decision is frozen — the data it
   * was generated from must not change underneath the signers.
   */
  get isIncorporationDocumentSentForSigning(): boolean {
    const incorporationDocument = this.incorporationDocument;
    return (
      incorporationDocument !== undefined &&
      isDocumentLocked(this.context, incorporationDocument.documentId)
    );
  }

  private get incorporationDocument() {
    return this.context.currentDocuments.find(
      (d) =>
        d.documentData?.type === DocumentType.custom_document &&
        d.documentData.customDocumentType === "incorporation",
    );
  }

  get stiftelsesdato(): string | undefined {
    const documentId = this.incorporationDocument?.documentId;
    if (!documentId || !isDocumentSigned(this.context, documentId)) {
      return undefined;
    }
    return this.context.documentStatuses.find((d) => d.documentId === documentId)?.signedAt;
  }

  get hasAuditor(): boolean {
    return this.auditorSelectModel.value && this.selectedAuditorCompany !== undefined;
  }

  get hasAccountant(): boolean {
    return this.accountantSelectModel.value && this.selectedAccountantCompany !== undefined;
  }

  get isValid(): boolean {
    const auditorValid = !this.hasAuditor || this.auditorEmailModel.isValid;
    const accountantValid = !this.hasAccountant || this.accountantEmailModel.isValid;
    const capitalConfirmerValid =
      this.selectedCapitalConfirmerCompany !== undefined &&
      this.capitalConfirmerRepresentativeEmailModel.isValid;
    const addressValid = this.businessAddressError === undefined;
    const boardValid =
      !this.isOtherBoard ||
      (this.boardChair !== undefined && this.duplicateBoardPersonError === undefined);
    const additionalSignersValid =
      this.duplicateSignerError === undefined &&
      this.additionalSigners.every(
        (s) =>
          s.kind === "role" ||
          (s.nameModel.isValid && s.emailModel.isValid && s.socialSecurityNumberModel.isValid),
      );
    return (
      this.companyNameModels.every((model) => model.isValid) &&
      this.hasSigners &&
      additionalSignersValid &&
      auditorValid &&
      accountantValid &&
      capitalConfirmerValid &&
      addressValid &&
      boardValid &&
      this.articlesModel.isValid &&
      this.notificationEmailModel.isValid &&
      this.invoiceEmailModel.isValid &&
      this.notificationPhoneModel.isValid
    );
  }

  validate(): boolean {
    this.validateCompanyNames();
    this.verifyBusinessAddress();
    this.signerModel.setError(
      this.hasSigners ? undefined : { key: "decisions.incorporation.signer-required" },
    );
    this.additionalSigners.forEach((s) => {
      if (s.kind === "custom") {
        s.nameModel.validate();
        s.emailModel.validate();
        s.socialSecurityNumberModel.validate();
      }
    });
    this.businessAddressModel.setError(this.businessAddressError);
    this.boardChairError =
      this.isOtherBoard && this.boardChair === undefined
        ? { key: "decisions.incorporation.board-chair-required" }
        : undefined;
    if (this.hasAuditor) {
      this.auditorEmailModel.validate();
    }
    if (this.hasAccountant) {
      this.accountantEmailModel.validate();
    }
    if (this.selectedCapitalConfirmerCompany === undefined) {
      this.capitalConfirmerValidationError = { key: "required" };
    } else {
      this.capitalConfirmerRepresentativeEmailModel.validate();
    }
    this.articlesModel.validate();
    this.notificationEmailModel.validate();
    this.invoiceEmailModel.validate();
    this.notificationPhoneModel.validate();
    return this.isValid;
  }

  setSelectedCapitalConfirmerCompany(company: SuggestedCompany) {
    this.capitalConfirmerSearchModel.setAndCommitInputValue(company.name);
    this.selectedCapitalConfirmerCompany = company;
    this.capitalConfirmerValidationError = undefined;
    defer(() => this.capitalConfirmerRepresentativeNameModel.focus());
  }

  clearSelectedCapitalConfirmerCompany() {
    this.capitalConfirmerSearchModel.clear();
    this.selectedCapitalConfirmerCompany = undefined;
    this.capitalConfirmerValidationError = undefined;
  }

  setCapitalConfirmerCompanySearch(input: string) {
    this.capitalConfirmerSearchModel.setInputValue(input);
    if (input.trim().length > 2) {
      this.searchCapitalConfirmerCompanies(input);
    } else {
      this.suggestedCapitalConfirmerCompanies = [];
    }
  }

  findCapitalConfirmerCompanies(input: string) {
    this.dispatch([
      { type: "dbot/search-companies", input, source: this.capitalConfirmerSearchSource },
    ]);
  }

  getCompanyNameAvailability(index: number): CompanyNameAvailability {
    return this.companyNameAvailability[index] ?? "unknown";
  }

  updateCompanyNameModels(n: number, initialValues: string[] = []) {
    const currentValues =
      initialValues.length > 0
        ? initialValues
        : this.companyNameModels.map((model) => model.validatedValue || model.inputValue);

    this.clearPendingCompanyNameChecks();
    this.companyNameModels = Array.from({ length: n }, (_, i) =>
      this.createCompanyNameModel(i, currentValues[i] ?? ""),
    );
    this.companyNameAvailability = Array.from({ length: n }, () => "unknown");
    this.companyNameProblems = Array.from({ length: n }, () => []);
    this.checkedCompanyNames = Array.from({ length: n }, () => "");

    if (this.isNamingAuto && initialValues.length === 0) {
      this.applyAutoNamingFromFirstName();
    }
  }

  get autoArticles(): ArticleSummary[] {
    return autoArticles({
      companyName: this.newCompanyName,
      statutoryPurpose:
        this.statutoryPurposeModel.validatedValue || this.statutoryPurposeModel.inputValue,
      shareCapital: this.calculatedShareCapital,
      numberOfShares: toNumber(this.numberOfSharesModel.parsedValue, 0),
      shareNominalValue: toNumber(this.shareNominalValueModel.parsedValue, 0),
    });
  }

  get calculatedShareCapital() {
    return (
      toNumber(this.shareNominalValueModel.parsedValue, 0) *
      toNumber(this.numberOfSharesModel.parsedValue, 0)
    );
  }

  createCompanyNameModel(index: number, value: string): StringFieldModel {
    return new StringFieldModel({
      value,
      required: true,
      onChange: () => this.handleCompanyNameChange(index),
      validator: (name) => this.validateCompanyName(index, name),
    });
  }

  handleCompanyNameChange(index: number): void {
    this.clearPendingCompanyNameChecks(index);
    this.setCompanyNameAvailability(index, "unknown");
    this.companyNameProblems[index] = [];

    if (index === 0 && this.isNamingAuto) {
      this.applyAutoNamingFromFirstName();
    }

    this.validateCompanyNames();
  }

  onCompanyNameBlur(index: number): void {
    this.companyNameModels[index]?.commit();

    if (this.isNamingAuto) {
      this.checkCompanyNameAvailabilityForIndexes(this.companyNameModels.map((_, i) => i));
    } else {
      this.checkCompanyNameAvailabilityForIndexes([index]);
    }
  }

  applyAutoNamingFromFirstName(): void {
    if (!this.companyNameModels.length) {
      return;
    }
    const firstName = this.newCompanyName;
    this.companyNameModels.slice(1).forEach((model, index) => {
      const generatedName = generateIncrementalCompanyName(firstName, index + 2);
      const currentName = model.validatedValue || model.inputValue;
      if (currentName !== generatedName) {
        model.setAndCommitInputValue(generatedName);
      }
    });
  }

  checkCompanyNameAvailabilityForIndexes(indexes: number[]): void {
    const events = indexes
      .map((index) => this.createCompanyNameCheckEvent(index))
      .filter((event): event is DbotEvent => event !== undefined);

    if (events.length > 0) {
      this.dispatch(events);
    }
  }

  createCompanyNameCheckEvent(index: number): DbotEvent | undefined {
    const model = this.companyNameModels[index];
    if (model) {
      const name = removeExtraSpaces(model.inputValue);
      this.clearPendingCompanyNameChecks(index);
      if (name) {
        const source = {};
        this.pendingCompanyNameChecks.set(source, {
          index,
          normalizedName: normalizeCompanyName(name),
        });
        this.setCompanyNameAvailability(index, "checking");
        this.companyNameProblems[index] = [];
        return { type: "dbot/check-company-name", name, source };
      }
      this.setCompanyNameAvailability(index, "unknown");
      this.companyNameProblems[index] = [];
    }
  }

  applyCompanyNameCheckResult(source: unknown, check: CompanyNameCheck): void {
    if (typeof source !== "object" || source === null) {
      return;
    }

    const pending = this.pendingCompanyNameChecks.get(source);
    if (!pending) {
      return;
    }
    this.pendingCompanyNameChecks.delete(source);

    const model = this.companyNameModels[pending.index];
    if (!model) {
      return;
    }

    const currentName = normalizeCompanyName(model.inputValue);
    if (!currentName || currentName !== pending.normalizedName) {
      return;
    }

    this.companyNameProblems[pending.index] = check.problems;
    this.checkedCompanyNames[pending.index] = pending.normalizedName;
    this.setCompanyNameAvailability(pending.index, availabilityForVerdict(check.verdict));
    this.validateCompanyNames();
  }

  clearPendingCompanyNameChecks(index?: number): void {
    if (index === undefined) {
      this.pendingCompanyNameChecks.clear();
      return;
    }

    const pendingEntries = Array.from(this.pendingCompanyNameChecks.entries());
    for (const [source, pending] of pendingEntries) {
      if (pending.index === index) {
        this.pendingCompanyNameChecks.delete(source);
      }
    }
  }

  setCompanyNameAvailability(index: number, availability: CompanyNameAvailability): void {
    this.companyNameAvailability[index] = availability;
  }

  get isNaceSearchable(): boolean {
    return this.industrySearchModel.inputValue.trim().length >= NACE_SEARCH_MIN_QUERY_LENGTH;
  }

  get isSearchingNace(): boolean {
    return this.naceSearchState === "searching";
  }

  get hasNoNaceMatches(): boolean {
    return (
      this.naceSearchState === "completed" &&
      this.isNaceSearchable &&
      this.suggestedNaceResults.length === 0
    );
  }

  get isCustomIndustry(): boolean {
    return this.selectedNace?.code === CUSTOM_INDUSTRY_CODE;
  }

  openCustomIndustryDialog() {
    this.industrySearchBeforeCustomDialog = this.industrySearchModel.inputValue;
    this.customIndustryModel = new StringFieldModel({
      value: this.isCustomIndustry ? this.selectedNace?.label : undefined,
      validator: validateCustomIndustry,
    });
  }

  setCustomIndustry(input: string) {
    const model = this.customIndustryModel;
    if (model) {
      model.setInputValue(input);
      model.validate();
    }
  }

  saveCustomIndustry() {
    const model = this.customIndustryModel;
    if (!model || model.isEmpty || !model.validate()) {
      return;
    }
    this.setSelectedNace({
      code: CUSTOM_INDUSTRY_CODE,
      label: model.inputValue.trim(),
      description: "",
      category: "",
    });
    this.customIndustryModel = undefined;
  }

  closeCustomIndustryDialog() {
    this.customIndustryModel = undefined;
    this.setNaceSearch(this.industrySearchBeforeCustomDialog);
  }

  setSelectedNace(nace: NaceSearchResult) {
    this.industrySearchModel.setAndCommitInputValue(nace.label);
    this.selectedNace = nace;
  }

  applyIndustryShortcut(shortcut: IndustryShortcut) {
    this.setSelectedNace(shortcut.industry);
    this.statutoryPurposeModel.setAndCommitInputValue(shortcut.statutoryPurpose);
  }

  clearSelectedNace() {
    this.industrySearchModel.clear();
    this.selectedNace = undefined;
    this.resetNaceSearch();
  }

  setNaceSearch(input: string) {
    this.industrySearchModel.setInputValue(input);
    if (this.isNaceSearchable) {
      this.naceSearchState = "searching";
      this.searchNace(input);
    } else {
      this.resetNaceSearch();
    }
  }

  private resetNaceSearch() {
    this.naceSearchState = "idle";
    this.suggestedNaceResults = [];
  }

  findNace(input: string) {
    this.dispatch([{ type: "dbot/search-nace", input, source: this }]);
  }

  setSelectedAuditorCompany(company: SuggestedCompany) {
    this.auditorSearchModel.setAndCommitInputValue(company.name);
    this.selectedAuditorCompany = company;
    this.checkAuditor();
    defer(() => this.auditorRepresentativeNameModel.focus());
  }

  clearSelectedAuditorCompany() {
    this.auditorSearchModel.clear();
    this.selectedAuditorCompany = undefined;
    this.auditorCheck = undefined;
    this.auditorCheckSource = undefined;
  }

  checkAuditor() {
    const organisationNumber = this.selectedAuditorCompany?.organizationnumber;
    if (organisationNumber === undefined) {
      this.auditorCheck = undefined;
      this.auditorCheckSource = undefined;
      return;
    }
    if (this.auditorCheck?.organisationNumber === organisationNumber) {
      return;
    }
    this.auditorCheckSource = {};
    this.auditorCheck = { organisationNumber, status: "checking" };
    this.dispatch([
      {
        type: "dbot/check-provider-validity",
        role: "auditor",
        organisationNumber,
        source: this.auditorCheckSource,
      },
    ]);
  }

  checkAccountant() {
    const organisationNumber = this.selectedAccountantCompany?.organizationnumber;
    if (organisationNumber === undefined) {
      this.accountantCheck = undefined;
      this.accountantCheckSource = undefined;
      return;
    }
    if (this.accountantCheck?.organisationNumber === organisationNumber) {
      return;
    }
    this.accountantCheckSource = {};
    this.accountantCheck = { organisationNumber, status: "checking" };
    this.dispatch([
      {
        type: "dbot/check-provider-validity",
        role: "accountant",
        organisationNumber,
        source: this.accountantCheckSource,
      },
    ]);
  }

  get auditorWarning(): TMessage | undefined {
    if (this.auditorCheck?.status !== "INVALID") {
      return undefined;
    }
    return {
      key: "decisions.incorporation.auditor-not-approved",
      params: { auditorCompanyName: this.selectedAuditorCompany?.name ?? "" },
    };
  }

  get accountantWarning(): TMessage | undefined {
    if (this.accountantCheck?.status !== "INVALID") {
      return undefined;
    }
    return {
      key: "decisions.incorporation.accountant-not-approved",
      params: { accountantCompanyName: this.selectedAccountantCompany?.name ?? "" },
    };
  }

  setAuditorCompanySearch(input: string) {
    this.auditorSearchModel.setInputValue(input);
    this.searchAuditorCompanies(input);
  }

  findAuditorCompanies(input: string) {
    this.dispatch([{ type: "dbot/search-companies", input, source: this }]);
  }

  setSelectedAccountantCompany(company: SuggestedCompany) {
    this.accountantSearchModel.setAndCommitInputValue(company.name);
    this.selectedAccountantCompany = company;
    this.checkAccountant();
    defer(() => this.accountantRepresentativeNameModel.focus());
  }

  clearSelectedAccountantCompany() {
    this.accountantSearchModel.clear();
    this.selectedAccountantCompany = undefined;
    this.accountantCheck = undefined;
    this.accountantCheckSource = undefined;
  }

  setAccountantCompanySearch(input: string) {
    this.accountantSearchModel.setInputValue(input);
    this.searchAccountantCompanies(input);
  }

  findAccountantCompanies(input: string) {
    this.dispatch([{ type: "dbot/search-companies", input, source: this.accountantSearchSource }]);
  }

  validateCompanyNames(): void {
    this.companyNameModels.forEach((model) => model.validate());
  }

  validateCompanyName(index: number, name: string): TMessage | undefined {
    return (
      validateCompanyNameSuffix(name) ??
      this.validateUniqueCompanyName(index, name) ??
      this.validateCompanyNameAvailability(index, name)
    );
  }

  validateUniqueCompanyName(index: number, name: string): TMessage | undefined {
    const normalizedName = normalizeCompanyName(name);
    if (!normalizedName) {
      return undefined;
    }

    const hasDuplicate = this.companyNameModels.some((model, i) => {
      if (i === index) {
        return false;
      }
      const otherName = normalizeCompanyName(model.inputValue);
      return Boolean(otherName) && otherName === normalizedName;
    });

    return hasDuplicate ? { key: "decisions.incorporation.company-name.duplicate" } : undefined;
  }

  validateCompanyNameAvailability(index: number, name: string): TMessage | undefined {
    const normalizedName = normalizeCompanyName(name);
    if (!normalizedName || normalizedName !== this.checkedCompanyNames[index]) {
      return undefined;
    }
    const problem = this.companyNameProblems[index]?.[0];
    return problem === undefined ? undefined : companyNameProblemMessage(problem);
  }
}

function formatBusinessAddress(street?: string, zip?: string, city?: string): string {
  const zipCity = [zip, city].filter(Boolean).join(" ");
  return [street, zipCity].filter(Boolean).join(", ");
}

export function formatMatrikkelAddress(address: CustomBusinessAddress): string {
  return formatBusinessAddress(address.addressText, address.postalCode, address.city);
}

export function matrikkelAddressId(address: CustomBusinessAddress): string {
  return [address.addressText, address.postalCode, address.objectType].join("|");
}

export function matrikkelObjectTypeLabel(objectType: MatrikkelObjectType): TMessage {
  return objectType === "VEGADRESSE"
    ? { key: "decisions.incorporation.address-objtype-vegadresse" }
    : { key: "decisions.incorporation.address-objtype-matrikkeladresse" };
}

function savedBusinessAddress(data?: Incorporation): CustomBusinessAddress | undefined {
  if (data?.addressIsSameAsParent !== false || !data.address || !data.zipCode) {
    return undefined;
  }
  return {
    addressText: data.address,
    postalCode: data.zipCode,
    city: data.city ?? "",
    objectType: data.addressObjType,
  };
}

function parentBoardAssignments(roles: CompanyRole[]): BoardAssignment[] {
  const assignments = roles.flatMap((member) => {
    const role = toAssignableRole(member.role);
    return role !== undefined && role !== RoleType.CEO
      ? [
          {
            id: newUuid(),
            role,
            name: getFullName(member),
            socialSecurityNumber: member.socialSecurityNumber?.trim() || undefined,
            personReference: toCompanyRoleReference(member.companyroleId),
          },
        ]
      : [];
  });
  return uniqBy(assignments, (a) => `${a.role}|${a.name}`);
}

function createBoardAssignments(data?: Incorporation): BoardAssignment[] {
  if (data?.boardIsSameAsParent !== false) {
    return [];
  }
  return (data.boardComposition ?? []).flatMap((member) => {
    const role = toAssignableRole(member.role);
    return role !== undefined
      ? [
          {
            id: member.id ?? newUuid(),
            role,
            name: removeExtraSpaces(member.name),
            socialSecurityNumber: member.socialSecurityNumber?.trim() || undefined,
            personReference: member.personReference,
            country: member.country,
          },
        ]
      : [];
  });
}

function toAssignableRole(role: RoleType): RoleType | undefined {
  switch (role) {
    case RoleType.CHAIRPERSON:
    case RoleType.CEO:
    case RoleType.MEMBER:
    case RoleType.DEPUTY_MEMBER:
      return role;
    case RoleType.DEPUTY_CHAIRPERSON:
      return RoleType.MEMBER;
    default:
      return undefined;
  }
}

function toIncorporationRole(assignment: BoardAssignment): IncorporationCompanyRole {
  return {
    id: assignment.id,
    role: assignment.role,
    name: assignment.name,
    socialSecurityNumber: assignment.socialSecurityNumber,
    personReference: assignment.personReference,
    country: assignment.country,
  };
}

export class AddBoardPersonModel {
  nameModel: StringFieldModel;
  countryModel: RadioFieldModel<{ code: string; name: string }>;
  socialSecurityNumberModel: StringFieldModel;
  roleModel: RadioFieldModel<RoleType>;

  constructor(
    locale: Language,
    roles: RoleType[],
    private readonly onSubmit: (person: BoardPerson, role: RoleType) => void,
    readonly onClose: () => void,
  ) {
    this.nameModel = new StringFieldModel({ required: true, validator: validateName });
    this.countryModel = makeCountryRadioFieldModel(locale);
    this.socialSecurityNumberModel = new StringFieldModel({
      required: this.countryModel.value.code === norway,
      validator: norwegianSocialSecurityNumberValidator,
    });
    this.roleModel = new RadioFieldModel({
      value: roles[0],
      options: roles,
      idFn: identity,
      labelFn: getRoleTypeLabel,
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setCountry(value: { code: string; name: string }) {
    this.countryModel.setValue(value);
    this.socialSecurityNumberModel.setRequired(value.code === norway);
    if (this.socialSecurityNumberModel.error) {
      this.socialSecurityNumberModel.validate();
    }
  }

  get isValid(): boolean {
    return (
      this.nameModel.isValid &&
      this.countryModel.isValid &&
      this.socialSecurityNumberModel.isValid &&
      this.roleModel.isValid
    );
  }

  validate(): boolean {
    this.nameModel.validate();
    this.countryModel.validate();
    this.socialSecurityNumberModel.validate();
    this.roleModel.validate();
    return this.isValid;
  }

  submit() {
    if (!this.validate()) {
      return;
    }
    this.onSubmit(
      {
        name: this.nameModel.validatedValue,
        socialSecurityNumber: this.socialSecurityNumberModel.validatedValue.trim() || undefined,
        country: this.countryModel.value.code,
      },
      this.roleModel.value,
    );
  }
}

export class AssignBoardRoleModel {
  roleModel: RadioFieldModel<RoleType>;

  constructor(
    readonly name: string,
    roles: RoleType[],
    initialRole: RoleType,
    private readonly onSubmit: (role: RoleType) => void,
    readonly onClose: () => void,
  ) {
    this.roleModel = new RadioFieldModel({
      value: initialRole,
      options: roles,
      idFn: identity,
      labelFn: getRoleTypeLabel,
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  submit() {
    this.onSubmit(this.roleModel.value);
  }
}

function parseShareTypeCode(value: ShareTypeCode | undefined): ShareTypeCode | undefined {
  if (!value) {
    return undefined;
  }
  return Object.values(ShareTypeCode).includes(value) ? value : undefined;
}

export function generateIncrementalCompanyName(baseName: string, sequenceNumber: number): string {
  const normalized = removeExtraSpaces(baseName);
  if (!normalized) {
    return "";
  }

  const trailingNumberPattern = /^(.*?)(\d+)(\D*)$/;
  const trailingNumberMatch = trailingNumberPattern.exec(normalized);
  if (trailingNumberMatch) {
    const [, prefix, currentNumber, suffix] = trailingNumberMatch;
    const nextNumber = Number.parseInt(currentNumber, 10) + sequenceNumber - 1;
    return removeExtraSpaces(`${prefix}${nextNumber}${suffix}`);
  }

  const companySuffixPattern = /^(.*?)(\s(?:AS|ASA|AB|A\/S|OY|OYJ))$/i;
  const companySuffixMatch = companySuffixPattern.exec(normalized);
  if (companySuffixMatch) {
    const [, prefix, suffix] = companySuffixMatch;
    return removeExtraSpaces(`${prefix} ${sequenceNumber}${suffix}`);
  }

  return `${normalized} ${sequenceNumber}`;
}

export function normalizeCompanyName(value: string): string {
  return removeExtraSpaces(value).toLowerCase();
}

function availabilityForVerdict(verdict: CompanyNameVerdict): CompanyNameAvailability {
  switch (verdict) {
    case "ACCEPTED":
      return "available";
    case "REJECTED":
      return "unavailable";
    default:
      return "unknown";
  }
}

export function companyNameProblemMessage(problem: CompanyNameProblem): TMessage {
  switch (problem) {
    case "INVALID_CHARACTERS":
      return { key: "decisions.incorporation.company-name.invalid-characters" };
    case "TOO_FEW_LETTERS":
      return { key: "decisions.incorporation.company-name.too-few-letters" };
    case "GEOGRAPHIC_NAME":
      return { key: "decisions.incorporation.company-name.geographic" };
    case "IDENTICAL_NAME":
      return { key: "decisions.companyName.match.brreg" };
    case "COMPANY_TYPE_SUFFIX":
      return { key: "decisions.incorporation.company-name.missing-as" };
    default:
      return { key: "decisions.incorporation.company-name.rejected" };
  }
}

export function validateCompanyNameSuffix(name: string): TMessage | undefined {
  const normalizedName = normalizeCompanyName(name);
  if (!normalizedName) {
    return undefined;
  }
  return normalizedName.endsWith(" as")
    ? undefined
    : { key: "decisions.incorporation.company-name.missing-as" };
}

function mapIncorporationAuditorToCompany(data?: Incorporation): SuggestedCompany | undefined {
  return mapCompanyContact(data?.auditor?.company);
}

function mapIncorporationAccountantToCompany(data?: Incorporation): SuggestedCompany | undefined {
  return mapCompanyContact(data?.accountant?.company);
}

function mapIncorporationCapitalConfirmerToCompany(
  data?: Incorporation,
): SuggestedCompany | undefined {
  return mapCompanyContact(data?.capitalConfirmer?.company);
}

function mapCompanyContact(contact?: CompanyContact): SuggestedCompany | undefined {
  return contact?.name && contact.organisationNumber
    ? {
        name: contact.name,
        organizationnumber: contact.organisationNumber,
        address: contact.businessAddress1,
        zip: contact.businessZip,
        city: contact.businessCity,
        sourceCountry: contact.businessCountry,
      }
    : undefined;
}
